import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { cn } from "../../utils/classNames";

const VIEWPORT_MARGIN = 12;
const TOOLTIP_GAP = 8;
const ARROW_SIZE = 8;
const ARROW_SAFE_OFFSET = 12;
const DEFAULT_MAX_WIDTH = 320;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function calculateTooltipLayout({
  anchorRect,
  tooltipRect,
  position = "top",
  viewportWidth,
  viewportHeight,
  margin = VIEWPORT_MARGIN,
  gap = TOOLTIP_GAP,
}) {
  const tooltipWidth = Math.min(tooltipRect.width, Math.max(0, viewportWidth - margin * 2));
  const tooltipHeight = tooltipRect.height;
  const centeredLeft = anchorRect.left + (anchorRect.width / 2) - (tooltipWidth / 2);
  const centeredTop = anchorRect.top + (anchorRect.height / 2) - (tooltipHeight / 2);
  let effectivePosition = position;

  if (position === "top" && anchorRect.top - tooltipHeight - gap < margin && anchorRect.bottom + tooltipHeight + gap <= viewportHeight - margin) {
    effectivePosition = "bottom";
  } else if (position === "bottom" && anchorRect.bottom + tooltipHeight + gap > viewportHeight - margin && anchorRect.top - tooltipHeight - gap >= margin) {
    effectivePosition = "top";
  } else if (position === "left" && anchorRect.left - tooltipWidth - gap < margin && anchorRect.right + tooltipWidth + gap <= viewportWidth - margin) {
    effectivePosition = "right";
  } else if (position === "right" && anchorRect.right + tooltipWidth + gap > viewportWidth - margin && anchorRect.left - tooltipWidth - gap >= margin) {
    effectivePosition = "left";
  }

  let top = 0;
  let left = 0;

  switch (effectivePosition) {
    case "bottom":
      top = anchorRect.bottom + gap;
      left = centeredLeft;
      break;
    case "left":
      top = centeredTop;
      left = anchorRect.left - tooltipWidth - gap;
      break;
    case "right":
      top = centeredTop;
      left = anchorRect.right + gap;
      break;
    case "top":
    default:
      top = anchorRect.top - tooltipHeight - gap;
      left = centeredLeft;
      break;
  }

  const arrowMargin = showArrowMargin(effectivePosition);
  left = clamp(
    left,
    margin + arrowMargin.left,
    Math.max(margin + arrowMargin.left, viewportWidth - tooltipWidth - margin - arrowMargin.right)
  );
  top = clamp(
    top,
    margin + arrowMargin.top,
    Math.max(margin + arrowMargin.top, viewportHeight - tooltipHeight - margin - arrowMargin.bottom)
  );

  const anchorCenterX = anchorRect.left + (anchorRect.width / 2);
  const anchorCenterY = anchorRect.top + (anchorRect.height / 2);
  const arrow = ["top", "bottom"].includes(effectivePosition)
    ? {
        left: clamp(anchorCenterX - left, ARROW_SAFE_OFFSET, Math.max(ARROW_SAFE_OFFSET, tooltipWidth - ARROW_SAFE_OFFSET)),
        top: null,
      }
    : {
        left: null,
        top: clamp(anchorCenterY - top, ARROW_SAFE_OFFSET, Math.max(ARROW_SAFE_OFFSET, tooltipHeight - ARROW_SAFE_OFFSET)),
      };

  return { top, left, position: effectivePosition, arrow };
}

function showArrowMargin(position) {
  return {
    top: position === "bottom" ? ARROW_SIZE : 0,
    right: position === "left" ? ARROW_SIZE : 0,
    bottom: position === "top" ? ARROW_SIZE : 0,
    left: position === "right" ? ARROW_SIZE : 0,
  };
}

const Tooltip = React.forwardRef(
  ({
    children,
    className,
    contentClassName,
    position = "top",
    showArrow = true,
    anchorRef,
    onClose: _onClose,
    ...props
  }, ref) => {
    const [layout, setLayout] = useState({
      top: 0,
      left: 0,
      position,
      arrow: { left: null, top: null },
      ready: false,
    });
    const tooltipRef = useRef(null);

    const updatePosition = useCallback(() => {
      if (anchorRef?.current && tooltipRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewport = window.visualViewport;
        const nextLayout = calculateTooltipLayout({
          anchorRect,
          tooltipRect,
          position,
          viewportWidth: viewport?.width ?? window.innerWidth,
          viewportHeight: viewport?.height ?? window.innerHeight,
        });

        setLayout({ ...nextLayout, ready: true });
      }
    }, [anchorRef, position]);

    useLayoutEffect(() => {
      const rafId = window.requestAnimationFrame(updatePosition);
      return () => window.cancelAnimationFrame(rafId);
    }, [updatePosition, children]);

    useEffect(() => {
      const viewport = window.visualViewport;
      window.addEventListener("resize", updatePosition);
      viewport?.addEventListener("resize", updatePosition);
      viewport?.addEventListener("scroll", updatePosition);
      return () => {
        window.removeEventListener("resize", updatePosition);
        viewport?.removeEventListener("resize", updatePosition);
        viewport?.removeEventListener("scroll", updatePosition);
      };
    }, [updatePosition]);

    useEffect(() => {
      window.addEventListener("scroll", updatePosition, true);
      return () => window.removeEventListener("scroll", updatePosition, true);
    }, [updatePosition]);

    const arrowClasses = {
      top: "top-full w-2 h-2 border-r border-b -mt-1",
      bottom: "bottom-full w-2 h-2 border-l border-t -mb-1",
      left: "left-full w-2 h-2 border-t border-r -ml-1",
      right: "right-full w-2 h-2 border-b border-l -mr-1",
    };

    // Runtime geometry stays inline so no stylesheet rule can override it;
    // visual styling lives in .sp-tooltip.
    const tooltipStyle = {
      position: "fixed",
      top: `${layout.top}px`,
      left: `${layout.left}px`,
      visibility: layout.ready ? "visible" : "hidden",
      "--tooltip-max-width": `${DEFAULT_MAX_WIDTH}px`,
      "--tooltip-viewport-gutter": `${VIEWPORT_MARGIN * 2}px`,
    };

    const arrowStyle = {
      "--tooltip-arrow-top": ["left", "right"].includes(layout.position) ? `${layout.arrow.top}px` : "auto",
      "--tooltip-arrow-left": ["top", "bottom"].includes(layout.position) ? `${layout.arrow.left}px` : "auto",
      "--tooltip-arrow-transform": ["top", "bottom"].includes(layout.position)
        ? "translateX(-50%) rotate(45deg)"
        : "translateY(-50%) rotate(45deg)",
    };

    const tooltip = (
      <motion.div
        role="tooltip"
        ref={(node) => {
          tooltipRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.25, ease: [0.0, 0.0, 0.2, 1] }}
        style={tooltipStyle}
        className={cn(
          "sp-tooltip border border-border p-4 shadow-tooltip pointer-events-none rounded-md",
          "bg-insight-screen text-foreground",
          className
        )}
        {...props}
      >
        <div className="absolute inset-0 hero-atom-grain pointer-events-none" />
        <div
          className={cn(
            "sp-tooltip__content relative z-10 text-body-small leading-relaxed text-foreground",
            "break-words",
            contentClassName
          )}
        >
          {children}
        </div>
        {showArrow && (
          <div
            className={cn(
              "sp-tooltip__arrow absolute bg-insight-screen border-border z-10",
              arrowClasses[layout.position]
            )}
            style={arrowStyle}
          />
        )}
      </motion.div>
    );

    return createPortal(tooltip, document.body);
  }
);

Tooltip.displayName = "Tooltip";

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  showArrow: PropTypes.bool,
  anchorRef: PropTypes.object,
  onClose: PropTypes.func,
};

export default Tooltip;
